import { KeyValuePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormField, MatSelectChange } from '@angular/material/select';
import { IonIcon } from '@ionic/angular/standalone';
import { PlotlySharedModule } from 'angular-plotly.js';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { SafeResizeHandlerDirective } from 'src/app/directives/plotly/reset-zoom-buttons/safe-resize-handler.directive';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { IReference, IReferenceMetadata } from 'src/app/model/thingie/data-processors';
import { ITask, IUserSetting } from 'src/app/model/thingie/task';
import { ThingieDataService } from 'src/app/services/data/thingie-data.service';
import { deepCopy, deepCopySync } from 'src/app/utility/deep-copy';
import { loadPlotly } from 'src/app/utility/plotly';
import { hovertemplateBase } from 'src/app/utility/plotly/hovertemplate-globals';
import { SubSink } from 'subsink';
import { PureFunctionCallPipe } from '../../../utility/pipes/pure-member-call/pure-member-call.pipe';
import { LoadingOverlayComponent } from '../../loading-overlay/loading-overlay.component';
import { SpinnerComponent } from '../../spinner/spinner.component';

export interface IProfile {
  type: string;
  duration: IDuration;
  volume: number;
  additionalInformation?: IAdditionalInformation;
}

export interface IAdditionalInformation {
  repetitions?: number;
  growthRate?: number;
}

export interface IDuration {
  day: number;
  hour: number;
  minute: number;
}

let minFlowRateOfTubingSizes: { [key: number]: number } = {
  13: 0.0033,
  14: 0.0156,
  16: 0.0286,
  19: 0.0477,
  25: 0.0933
};

let maxFlowRateOfTubingSizes: { [key: number]: number } = {
  13: 11.55,
  14: 54.6,
  16: 101.1,
  19: 166.95,
  25: 326.54
};

interface ISegmentError {
  minVolumeFlowNotReached: boolean;
  maxVolumeFlowExceed: boolean;
  minVolumeFlowPerShot: boolean;
  noTypeSelected: boolean;
  additionalsInvalid: boolean;
}

interface IProfileStatus {
  totalVolume: number;
  hasError: boolean;
  maxVolumeExceeded: boolean;
  segmentErrors: (ISegmentError | null)[];
}
export interface ILisProfileGeneratorOptions {
  task: ITask;
  inputKey: string;

  readonly: boolean;
}

@Component({
  selector: 'app-lis-profile-generator',
  templateUrl: './lis-profile-generator.component.html',
  styleUrls: ['./lis-profile-generator.component.scss'],
  imports: [
    PlotlySharedModule,
    MaterialImportsModule,
    IonIcon,
    FormsModule,
    MatOptionModule,
    LoadingOverlayComponent,
    SpinnerComponent,
    KeyValuePipe,
    PureFunctionCallPipe,
    SafeResizeHandlerDirective,
  ]
})
export class LisProfileGeneratorComponent implements OnInit, OnDestroy {
  @Input()
  public set options(opts: ILisProfileGeneratorOptions) {
    this.inputKey = opts.inputKey;
    this.task = deepCopySync(opts.task);
    this.readonly = opts.readonly;
  }

  @Output() public profileChange = new EventEmitter<any[][]>();

  protected valueType = {
    delay: 'Delay',
    singleShot: 'Single-Shot',
    constant: 'Constant',
    multiShot: 'Multi-Shot',
    exponential: 'Exponential'
  };

  protected fieldType = {
    day: 'day',
    hour: 'hour',
    minute: 'minute',
    volume: 'volume',
    repetitions: 'repetitions',
    growthRate: 'growthRate'
  };

  private timeArray: any[] = [];
  private volArray: number[] = [];
  private selectedValueType = '';
  private taskCopy?: ITask;
  protected profiles: IProfile[] = [];
  protected loadingData = false;
  protected traceForPlotly = {};
  protected layout = {
    height: 350,
    margin: {
      l: 60,
      r: 60,
      b: 70,
      t: 10,
      pad: 4
    },
    xaxis: {
      title: {
        text: 'Time since profile start [hours]',
      },
    },
    yaxis: {
      title: {
        text: 'Volume [ml]'
      },
    }
  };

  protected plotlyConfig = {
    displaylogo: false,
    displayModeBar: true
  };

  private inputKey!: string;
  protected readonly = false;
  private task!: ITask;
  private subscriptions = new SubSink();

  protected profileStatus: IProfileStatus = {
    totalVolume: 0,
    hasError: false,
    maxVolumeExceeded: false,
    segmentErrors: []
  };

  protected noTypeSelectedMessage = 'Please select a type.';
  protected maximumVolumeExceededMessage = 'The maximum total dispensable volume has been exceeded.';

  protected maxTotalVolume = 25000;
  protected unknownMaxVolume = false;

  protected currentMaxFlowLimitTooltip = '';
  protected currentMinFlowLimitTooltip = '';

  protected volumeUnit = 'µl';

  public get valid(): boolean {
    return !this.profileStatus.hasError;
  }

  constructor(
    private thingieDataService: ThingieDataService,
  ) {
    void loadPlotly();
    this.profiles = [];

    // To be used with the pure member function call pipe
    this.isDisabledInputField = this.isDisabledInputField.bind(this);
  }

  public async ngOnInit() {
    if (!this.task) {
      throw new Error(`Input 'options' is required but not provided.`);
    }

    // Determine maximum total volume
    const maxVolume = (this.task.inputs.configFillingVolume as IUserSetting)?.value;

    if (maxVolume !== undefined) {
      this.maxTotalVolume = maxVolume as number;
    } else {
      this.unknownMaxVolume = true;
    }

    // Determine volume unit. It is defined as an hidden input in the task template
    const volumeUnit = (this.task.inputs.volumeUnit as IUserSetting)?.value;
    if (volumeUnit !== undefined && typeof volumeUnit === 'string') {
      this.volumeUnit = volumeUnit as string;
    }

    // Keep maxTotalVolume in the same unit as inputs/summary
    if (!this.unknownMaxVolume && this.volumeUnit === 'ml') {
      this.maxTotalVolume = this.maxTotalVolume / 1000; // µl -> ml
    }

    this.initializeProfile((this.task.inputs[this.inputKey] as IUserSetting).value as any[][]);

    this.plotProfileIfValid();
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private initializeProfile(value: any[][]) {
    // Reset profiles
    this.profiles = [];
    if (value.length === 0) {
      // have at least one empty segment entry when no value is present
      this.addNewSegment();
    }

    value.forEach((val: any[]) => {
      const day = Math.floor(val[1] / (3600 * 24));
      const hour = Math.floor(val[1] % (3600 * 24) / 3600);
      const minute = Math.floor(val[1] % 3600 / 60);
      this.addProfileSegment({
        type: val[0],
        day,
        hour,
        minute,
        volume: val[2],
        repetitions: val[3],
        growthRate: val[4]
      });
    });
  }

  public addNewSegment() {
    this.addProfileSegment({
      day: 0,
      growthRate: undefined,
      hour: 0,
      minute: 0,
      repetitions: undefined,
      type: '',
      volume: 0
    });

    this.plotProfileIfValid();
  }

  private addProfileSegment(opts: {
    type: string;
    day: number;
    hour: number;
    minute: number;
    volume: number;
    repetitions?: number;
    growthRate?: number;
  }) {
    // add new row with initial values
    const newSegment: IProfile = {
      type: opts.type,
      duration: {
        day: opts.day,
        hour: opts.hour,
        minute: opts.minute
      } as IDuration,
      volume: opts.volume,
      additionalInformation: {
        growthRate: opts.growthRate === 0 ? 2 : opts.growthRate,
        repetitions: opts.repetitions
      } as IAdditionalInformation
    };

    this.profiles.push(newSegment);
  }

  protected removeSegment(arrayIndex: number) {
    this.profiles.splice(arrayIndex, 1);
    this.plotProfileIfValid();
  }

  protected rearrangeSegment(index: number, mode: string) {
    if (mode === 'up' && index !== 0) {
      this.profiles = this.moveArrayItemToNewIndex(index, index - 1);
    }
    if (mode === 'down' && index !== this.profiles.length - 1) {
      this.profiles = this.moveArrayItemToNewIndex(index, index + 1);
    }

    this.plotProfileIfValid();
  }

  private moveArrayItemToNewIndex(old_index: number, new_index: number) {
    this.profiles.splice(new_index, 0, this.profiles.splice(old_index, 1)[0]);
    return this.profiles;
  }

  protected trackChange(fieldType: string, value: number | undefined, index: number, formField?: MatFormField) {
    if (value !== undefined) {
      let finalValue = value;
      switch (fieldType) {
        case this.fieldType.day:
          this.profiles[index].duration.day = value;
          this.translateSegmentTimeValues(index);
          finalValue = this.profiles[index].duration.day;
          break;
        case this.fieldType.hour:
          this.profiles[index].duration.hour = value;
          this.translateSegmentTimeValues(index);
          finalValue = this.profiles[index].duration.hour;
          break;
        case this.fieldType.minute:
          this.profiles[index].duration.minute = value;
          this.translateSegmentTimeValues(index);
          finalValue = this.profiles[index].duration.minute;
          break;
        case this.fieldType.volume:
          this.profiles[index].volume = value;
          break;
        case this.fieldType.repetitions:
          this.profiles[index].additionalInformation!.repetitions = value;
          break;
        case this.fieldType.growthRate:
          this.profiles[index].additionalInformation!.growthRate = value < 0 ? 0 : value;
          break;
      }

      if (formField !== undefined) {
        formField._control.value = finalValue;
      }

      this.plotProfileIfValid();
    }
  }

  protected getSegmentTooltip(index: number) {
    const segment = this.profileStatus.segmentErrors[index];

    const messages: string[] = [];

    if (segment) {
      if (segment.maxVolumeFlowExceed) {
        messages.push('Maximum volume flow rate exceeds ' + this.currentMaxFlowLimitTooltip);
      }

      if (segment.minVolumeFlowNotReached) {
        if (segment.minVolumeFlowPerShot) {
          messages.push(
            'Minimum dispensable volume per repetition is ' + this.currentMinFlowLimitTooltip + '.'
            + ' The volume dispensed per shot is calculated by dividing the total volume'
            + ' set in this step by the number of repetitions.'
          );
        } else {
          messages.push('Minimum dispensable volume is ' + this.currentMinFlowLimitTooltip);
        }
      }

      if (this.profileStatus.maxVolumeExceeded) {
        messages.push(this.maximumVolumeExceededMessage);
      }
    }

    return messages.join('\n');
  }

  public static async getLisProfileDataTrace(thingieDataService: ThingieDataService, task: ITask) {
    const taskCopy = await deepCopy(task);

    let volumeUnit = (taskCopy.inputs.volumeUnit as IUserSetting)?.value as string;
    if (volumeUnit === undefined) {
      volumeUnit = 'µl';
    }

    // request a preview by setting the 'preview' input of the dataprocessor to 'true'
    const lisProfileDataProcessor = taskCopy.dataProcessors.find(dataprocessor => dataprocessor.routine === 'lissegmentedprofile');
    const previewInput = lisProfileDataProcessor?.inputs.find(input => input.as === 'preview');
    const tickDataprocessorInput = lisProfileDataProcessor?.inputs.find(input => input.as === 'tick_input');
    if (lisProfileDataProcessor === undefined || previewInput === undefined || previewInput.source !== 'constant' || tickDataprocessorInput === undefined || tickDataprocessorInput.source !== 'reference') {
      throw new Error('Malformed LIS template!');
    }
    previewInput.value = true;

    // need one tick period of data
    const tickInput = taskCopy.inputs[tickDataprocessorInput.id];
    if (tickInput.source !== 'tick') {
      throw new Error('Malformed LIS template!');
    }
    const start = DateTime.fromMillis(1000);
    taskCopy.started = start.toISO();
    taskCopy.stopped = start.plus({ seconds: tickInput.interval_s }).toISO();

    // override start and stop condition, if it exists
    const startInputIdx = lisProfileDataProcessor?.inputs.findIndex(input => input.as === 'startIndicator');
    if (lisProfileDataProcessor !== undefined && startInputIdx !== -1) {
      lisProfileDataProcessor.inputs[startInputIdx] = {
        source: 'constant',
        as: 'startIndicator',
        requiredNew: false,
        value: 1
      };
    }
    const stopInputIdx = lisProfileDataProcessor?.inputs.findIndex(input => input.as === 'stopIndicator');
    if (lisProfileDataProcessor !== undefined && stopInputIdx !== -1) {
      lisProfileDataProcessor.inputs[stopInputIdx] = {
        source: 'constant',
        as: 'stopIndicator',
        requiredNew: false,
        value: false
      };
    }

    const requiredInputs = lisProfileDataProcessor.inputs
      .filter((input): input is IReference | IReferenceMetadata => input.source === 'reference' || input.source === 'referenceMetadata')
      .map(input => input.id);
    const inputs: ITask['inputs'] = {};
    for (const input of requiredInputs) {
      inputs[input] = taskCopy.inputs[input];
    }
    const outputName = lisProfileDataProcessor.outputs[0];
    taskCopy.inputs = inputs;
    taskCopy.dataProcessors = [ lisProfileDataProcessor];
    taskCopy.outputs = {};
    taskCopy.outputs[outputName] = { items: {} };

    return (await lastValueFrom(thingieDataService
      .oneTimeEvaluation(taskCopy, undefined)
      .pipe(map(data => {
        let timeArray: number[]  = [];  // X-axis data
        const volArray: number[] = [];  // Y-axis data

        // preview will be in 'preview' field of first data point of 'setpoint' series
        const points = data.find((series: any) => series.output === outputName)?.points?.[0]?.data?.preview;
        points?.forEach(([time, volume]: [number, number]) => {
          // dataElems are [time in ms, volume in µl] pairs

          // add volume to axis in [ml] unit. Do transformation if it's provided in a different unit
          if (volumeUnit === 'µl') {
            volArray.push(volume / 1000);
          } else {
            volArray.push(volume);
          }
          // add time [h] to axis
          timeArray.push(time / 3600);
        });


        //// Determine time unit for the X-axis

        let unit = 'days';
        if (timeArray.length) {
          const totalDuration = timeArray[timeArray.length - 1];
          if (totalDuration < 24 * 7) {
            unit = 'hours';
          }

          if (totalDuration < 1) {
            unit = 'minutes';
          }
        } else {
          unit = 'minutes';
        }

        if (unit === 'days') {
          timeArray = timeArray.map(val => val / 24);
        } else if (unit === 'minutes') {
          timeArray = timeArray.map(val => val * 60);
        }


        //// Prepare data for plotly

        const traceForPlotly = {
          type: 'scatter',
          x: timeArray,
          y: volArray,
          mode: 'lines',
          name: 'Volume',
          line: {
            color: 'rgb(219, 64, 82)',
            width: 3
          },
          hovertemplate: hovertemplateBase
        };

        return { trace: traceForPlotly, unit };
      }))))!;
  }

  private plotDataWithOutput = debounce(
    async () => {
      // obtain matrix and saving as new value for input profile
      (this.task.inputs[this.inputKey] as IUserSetting).value = this.saveLisProfile();

      this.loadingData = true;

      try {
        this.loadingData = true; // start loading spinner
        const { trace, unit } = await LisProfileGeneratorComponent.getLisProfileDataTrace(
          this.thingieDataService,
          this.task
        );
        this.traceForPlotly = trace;
        this.layout.xaxis.title.text = `Time since profile start [${unit}]`;
      } finally {
        this.loadingData = false;
      }
    },
    360, // 360 millseconds debounce time
    { leading: true } // Don't debounce the first call
  );

  protected valueSelection(event: MatSelectChange, i: number) {
    this.profiles[i].type = event.value;

    if (event.value === this.valueType.delay) {
      this.profiles[i].volume = 0;
    }

    if (event.value === this.valueType.singleShot) {
      this.profiles[i].duration.day = this.profiles[i].duration.hour = this.profiles[i].duration.minute = 0;
    }

    this.validateProfile();
    this.plotProfileIfValid();
  }

  private updateProfile() {
    this.profileChange.emit(this.saveLisProfile());
  }

  // disable profile input based on type selected
  protected isDisabledInputField(selectedType: string, fieldSelected: string) {
    switch (selectedType) {
      case this.valueType.delay:
        if (fieldSelected === this.fieldType.volume) {
          return true;
        } else {
          return false;
        }
      case this.valueType.singleShot:
        if (
          fieldSelected === this.fieldType.day ||
          fieldSelected === this.fieldType.hour ||
          fieldSelected === this.fieldType.minute
        ) {
          return true;
        } else {
          return false;
        }
      default:
        return false;
    }
  }

  // generate two dimentional matrix based on input of each rows
  private saveLisProfile() {
    const valueOuterArray: any[][] = [];

    this.profiles.forEach(profile => {
      const valueInnerArray: any[] = [];
      valueInnerArray.push(profile.type);

      const durationInSeconds = profile.duration.day * 86400 + profile.duration.hour * 3600 + profile.duration.minute * 60;
      valueInnerArray.push(durationInSeconds);
      valueInnerArray.push(profile.volume);
      valueInnerArray.push(profile.additionalInformation?.repetitions);
      valueInnerArray.push(profile.additionalInformation?.growthRate);

      valueOuterArray.push(valueInnerArray);
    });

    return valueOuterArray;
  }

  public validateProfile() {
    const profileStatus: IProfileStatus = {
      totalVolume: 0,
      hasError: false,
      maxVolumeExceeded: false,
      segmentErrors: []
    };

    for (let i = 0; i < this.profiles.length; ++i) {
      const segment = this.profiles[i];

      profileStatus.totalVolume += segment.volume;

      profileStatus.segmentErrors[i] = null;

      const noTypeSelected = segment.type === '';
      // For single-shots timing is disabled and always 0 as LIS tries to dispense as fast as possible.
      let maxFlowCheckPassed = false;
      let minFlowCheckPassed = false;
      if ((this.task.inputs.tubingSize as IUserSetting)?.value !== undefined) {
        // Shenchen peristaltic pump
        if (segment.type === this.valueType.singleShot) {
          maxFlowCheckPassed = true;
          minFlowCheckPassed = true;
        } else {
          maxFlowCheckPassed = this.checkMaxFlowRate(segment);
          minFlowCheckPassed = this.checkMinFlowRate(segment);
        }
      } else {
        maxFlowCheckPassed = segment.type === this.valueType.singleShot ? true : this.checkMaxVolumeFlow(segment);
        minFlowCheckPassed = this.checkMinVolumeFlow(segment);
      }
      const minFlowMultishotCheckFailed = !minFlowCheckPassed && segment.type === this.valueType.multiShot;
      const additionalsValid = this.checkAdditionalRequirements(segment);

      if (!maxFlowCheckPassed || !minFlowCheckPassed || noTypeSelected || !additionalsValid) {
        profileStatus.segmentErrors[i] = {
          maxVolumeFlowExceed: !maxFlowCheckPassed,
          minVolumeFlowNotReached: !minFlowCheckPassed,
          noTypeSelected,
          additionalsInvalid: !additionalsValid,
          minVolumeFlowPerShot: minFlowMultishotCheckFailed
        };
        profileStatus.hasError = true;
      }
    }

    if (!this.unknownMaxVolume && profileStatus.totalVolume > this.maxTotalVolume) {
      profileStatus.maxVolumeExceeded = true;
      profileStatus.hasError = true;
    }

    this.profileStatus = profileStatus;

    return !profileStatus.hasError;
  }

  private checkAdditionalRequirements(segment: IProfile) {
    if (
      segment.type === this.valueType.exponential
      && (segment.additionalInformation!.growthRate === undefined
        || segment.additionalInformation!.growthRate === null
        || segment.additionalInformation!.growthRate === 0)
    ) {
      return false;
    } else if (
      segment.type === this.valueType.multiShot
      && (segment.additionalInformation!.repetitions === null
        || segment.additionalInformation!.repetitions === 0
        || segment.additionalInformation!.repetitions === 1
        || !this.isNaturalNumber(segment.additionalInformation!.repetitions))
    ) {
      return false;
    }

    return true;
  }

  private isNaturalNumber(nr: number | undefined): boolean {
    if (typeof nr !== 'number' || nr === undefined) {
      return false;
    }
    return (nr >= 0.0) && (Math.floor(nr) === nr) && nr !== Infinity;
  }

  private checkMaxVolumeFlow(segment: IProfile): boolean {
    // Check maximum dispensable volume flow
    const maxVolumeFlow = 1000;
    this.currentMaxFlowLimitTooltip = maxVolumeFlow + ' µl/min';

    if (
      segment.duration.minute === 0
      && segment.duration.hour === 0
      && segment.duration.day === 0
    ) {
      return false;
    }

    if (segment.volume / this.durationToMinutes(segment.duration) > maxVolumeFlow) {
      return false;
    }

    return true;
  }

  private checkMinVolumeFlow(segment: IProfile): boolean {
    const minimumFlow = 100;
    this.currentMinFlowLimitTooltip = minimumFlow  + ' µl/min';

    if (segment.type === this.valueType.delay) {
      return true;
    }

    if (segment.type === this.valueType.multiShot) {
      return (segment.volume / segment.additionalInformation!.repetitions!) >= minimumFlow;
    }

    return segment.volume >= minimumFlow;
  }

  //Special implementation for Shenchen peristaltic pump
  private checkMaxFlowRate(segment: IProfile): boolean {
    const tubingSize = Number((this.task.inputs.tubingSize as IUserSetting)?.value);
    const maxFlowRate = maxFlowRateOfTubingSizes[tubingSize];
    this.currentMaxFlowLimitTooltip = maxFlowRate + ' ml/min';

    if (!Number.isFinite(maxFlowRate)) {
      // Unknown tubing size -> do not block
      return true;
    }

    if (
      segment.duration.minute === 0
      && segment.duration.hour === 0
      && segment.duration.day === 0
    ) {
      return false;
    }
    const minutes = this.durationToMinutes(segment.duration);
    const volumeMl = this.volumeUnit === 'µl' ? (segment.volume / 1000) : segment.volume;
    return (volumeMl / minutes) <= maxFlowRate;
  }

  //Special implementation for Shenchen peristaltic pump
  private checkMinFlowRate(segment: IProfile): boolean {
    const tubingSize = Number((this.task.inputs.tubingSize as IUserSetting)?.value);
    const minFlowRate = minFlowRateOfTubingSizes[tubingSize];
    this.currentMinFlowLimitTooltip = minFlowRate + ' ml/min';

    if (!Number.isFinite(minFlowRate)) {
      return true;
    }

    if (
      segment.duration.minute === 0
      && segment.duration.hour === 0
      && segment.duration.day === 0
    ) {
      return false;
    }

    if (segment.type === this.valueType.delay) {
      return true;
    }

    const minutes = this.durationToMinutes(segment.duration);
    const volumeMl = this.volumeUnit === 'µl' ? (segment.volume / 1000) : segment.volume;
    if (segment.type === this.valueType.multiShot) {
      const perShotMl = volumeMl / (segment.additionalInformation!.repetitions!);
      return (perShotMl / minutes) >= minFlowRate;
    }
    return (volumeMl / minutes) >= minFlowRate;
  }

  private durationToMinutes(duration: IDuration): number {
    return duration.day * 1440 + duration.hour * 60 + duration.minute;
  }

  private translateSegmentTimeValues(index: number) {
    const minutesPerHour = 60;
    const minutesPerDay = 24 * minutesPerHour;

    const segment = this.profiles[index];

    const totalMinutes = segment.duration.minute
      + segment.duration.hour * minutesPerHour
      + segment.duration.day * minutesPerDay;

    const days = Math.floor(totalMinutes / minutesPerDay);
    const hours = Math.floor(((totalMinutes % minutesPerDay) / minutesPerHour));
    const minutes = ((totalMinutes % minutesPerDay) % minutesPerHour);

    segment.duration.day = days >= 0 ? days : 0;
    segment.duration.hour = hours >= 0 ? hours : 0;
    segment.duration.minute = minutes >= 0 ? minutes : 0;
  }

  /**
   * Checks segments for errors, shows those errors - if any -
   * and returns the plot data.
   *
   * @returns Plot data
   */
  private plotProfileIfValid() {
    if (this.validateProfile()) {
      this.updateProfile();
      void this.plotDataWithOutput();
    }
  }
}
